import { useRef, useState, type KeyboardEvent } from "react";
import {
  ArrowDown,
  ArrowRight,
  Check,
  Code2,
  Download,
  FileCode2,
  GitBranch,
  GitCommitHorizontal,
  History,
  LockKeyhole,
  Plus,
  Users,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { FaqAccordion } from "@/components/accordion";
import { LocaleLink } from "@/components/locale-link";
import { YouTubeFacade } from "@/components/youtube-facade";
import { useTranslation } from "@/common/providers/i18n-provider";
import { getLocaleDirection } from "@/config/locales";
import { GIT_DEMO_URL, SIGN_UP_AUTH } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import { GIT_FAQ_KEYS } from "@/pages/git-content";
import "@/styles/git.css";

const PREVIEW_VIEWS = ["ledger", "history", "changes"] as const;

/** A deliberately illustrative ledger, not a screenshot of private customer data. */
function LedgerPreview() {
  const { t, locale } = useTranslation();
  const [active, setActive] =
    useState<(typeof PREVIEW_VIEWS)[number]>("ledger");
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const invoice = t("git.design.invoiceCode");
  const expense = t("git.design.expenseCode");
  const dateFormat = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  function navigateTabs(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const direction = getLocaleDirection(locale) === "rtl" ? -1 : 1;
    let next: number;
    switch (event.key) {
      case "ArrowRight":
        next =
          (index + direction + PREVIEW_VIEWS.length) % PREVIEW_VIEWS.length;
        break;
      case "ArrowLeft":
        next =
          (index - direction + PREVIEW_VIEWS.length) % PREVIEW_VIEWS.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = PREVIEW_VIEWS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActive(PREVIEW_VIEWS[next]);
    tabs.current[next]?.focus();
  }

  return (
    <figure className="git-preview-stage">
      <div className="git-preview-orbit" aria-hidden="true">
        <GitBranch strokeWidth={1} />
      </div>
      <div className="git-workbench">
        <div className="git-workbench-header">
          <span className="git-file-label" dir="ltr">
            <FileCode2 size={17} aria-hidden="true" />
            {t("git.design.filename")}
          </span>
          <span className="git-branch-label" dir="ltr">
            <GitBranch size={13} aria-hidden="true" />
            {t("git.design.branch")}
          </span>
        </div>
        <div
          className="git-preview-tabs"
          role="tablist"
          aria-label={t("git.design.preview")}
        >
          {PREVIEW_VIEWS.map((view, index) => (
            <button
              key={view}
              ref={(element) => {
                tabs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`git-tab-${view}`}
              aria-controls={`git-panel-${view}`}
              aria-selected={active === view}
              tabIndex={active === view ? 0 : -1}
              onClick={() => setActive(view)}
              onKeyDown={(event) => navigateTabs(event, index)}
            >
              {t(`git.design.${view}`)}
            </button>
          ))}
        </div>
        <div
          id="git-panel-ledger"
          role="tabpanel"
          aria-labelledby="git-tab-ledger"
          tabIndex={0}
          hidden={active !== "ledger"}
          className="git-preview-panel"
        >
          <pre className="git-ledger-code" dir="ltr">
            <code>
              {`${invoice}\n\n${expense}`.split("\n").map((line, index) => (
                <span
                  className={/^\d{4}-/.test(line) ? "git-code-date" : undefined}
                  key={index}
                >
                  <span className="git-line-number" aria-hidden="true">
                    {index + 1}
                  </span>
                  {line}
                  {"\n"}
                </span>
              ))}
            </code>
          </pre>
        </div>
        <div
          id="git-panel-history"
          role="tabpanel"
          aria-labelledby="git-tab-history"
          tabIndex={0}
          hidden={active !== "history"}
          className="git-preview-panel"
        >
          <ol className="git-commit-history">
            {[
              ["git.design.commitExpense", expense.slice(0, 10)],
              ["git.design.commitInvoice", invoice.slice(0, 10)],
              ["git.design.commitOpen", invoice.slice(0, 10)],
            ].map(([key, date]) => (
              <li key={key}>
                <GitCommitHorizontal size={22} aria-hidden="true" />
                <div>
                  <p>{t(key)}</p>
                  <time dateTime={date}>
                    {dateFormat.format(new Date(`${date}T00:00:00Z`))}
                  </time>
                </div>
              </li>
            ))}
          </ol>
          <p className="git-panel-note">{t("git.design.historyHint")}</p>
        </div>
        <div
          id="git-panel-changes"
          role="tabpanel"
          aria-labelledby="git-tab-changes"
          tabIndex={0}
          hidden={active !== "changes"}
          className="git-preview-panel"
        >
          <p className="git-diff-caption">{t("git.design.commitExpense")}</p>
          <pre className="git-ledger-code git-diff-code" dir="ltr">
            <code>
              {expense.split("\n").map((line, index) => (
                <span key={index}>
                  <span className="git-diff-plus">+ </span>
                  {line}
                  {"\n"}
                </span>
              ))}
            </code>
          </pre>
          <p className="git-panel-note">{t("git.design.diffSummary")}</p>
        </div>
        <div className="git-workbench-footer">
          <GitCommitHorizontal size={17} aria-hidden="true" />
          {t("git.design.saved")}
        </div>
      </div>
      <figcaption className="git-preview-caption">
        <span aria-hidden="true" />
        {t("git.design.example")}
      </figcaption>
    </figure>
  );
}

const WORKFLOW_STEPS = [
  {
    title: "git.cloneYourLedger",
    description: "git.cloneYourLedgerRepositoryToYour",
    code: "git.design.cloneCode",
  },
  {
    title: "git.editLocally",
    description: "git.useYourFavoriteEditorVsCode",
    code: "git.design.editCode",
  },
  {
    title: "git.pushChangesBack",
    description: "git.commitYourChangesAndPushTo",
    code: "git.design.pushCode",
  },
] as const;

const BENEFITS = [
  {
    icon: Code2,
    title: "git.toolFreedom",
    description: "git.editWithAnyEditorVsCode",
  },
  {
    icon: History,
    title: "git.completeAuditTrail",
    description: "git.gitShowsExactlyWhoChangedWhat",
  },
  {
    icon: Download,
    title: "git.distributedBackups",
    description: "git.everyCloneIsACompleteBackup",
  },
  {
    icon: Users,
    title: "git.collaborationReady",
    description: "git.useBranchesPullRequestsAndCode",
  },
] as const;

/** Both locale route trees share this body and the FAQ keys used by their SEO. */
export function GitComponent() {
  const { t } = useTranslation();
  return (
    <div className="git-page">
      <AppHeader />
      <main id="main">
        <section className="git-hero" aria-labelledby="git-title">
          <div className="git-container git-hero-layout">
            <div className="git-hero-copy">
              <p className="git-eyebrow">
                <GitBranch size={17} aria-hidden="true" />
                {t("git.versionControlForYourFinances")}
              </p>
              <h1 id="git-title" className="font-display-brand git-hero-title">
                {t("git.yourFinancesYourGitRepositoryYour")}
              </h1>
              <p className="git-hero-description">
                {t("git.manageYourAccountingLedgerWithStandard")}
              </p>
              <div className="git-actions">
                <a
                  href={productUrl(SIGN_UP_AUTH, "git")}
                  className="git-button git-button-primary"
                >
                  {t("homepage.startFree")}
                  <ArrowRight size={17} aria-hidden="true" />
                </a>
                <a
                  href={productUrl(GIT_DEMO_URL, "git")}
                  className="git-text-link"
                >
                  {t("git.viewGitServer")}
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
              <p className="git-assurance">
                <Check size={15} aria-hidden="true" />
                {t("git.free30DayTrialNoCredit")}
              </p>
            </div>
            <LedgerPreview />
          </div>
          <div className="git-container">
            <ul className="git-principles">
              {[
                { icon: LockKeyhole, key: "git.dataOwnership" },
                { icon: GitBranch, key: "git.gitProtocol" },
                { icon: History, key: "git.versionHistory" },
              ].map(({ icon: Icon, key }) => (
                <li key={key}>
                  <Icon size={18} aria-hidden="true" />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section
          className="git-container git-section"
          aria-labelledby="git-workflow-title"
        >
          <div className="git-section-intro">
            <div>
              <p className="git-eyebrow">{t("git.design.workflowEyebrow")}</p>
              <h2 id="git-workflow-title" className="git-heading">
                {t("git.design.workflowTitle")}
              </h2>
            </div>
            <p className="git-section-description">
              {t("git.threeSimpleStepsToVersionControlled")}
            </p>
          </div>
          <ol className="git-workflow">
            {WORKFLOW_STEPS.map((step, index) => (
              <li key={step.title}>
                <div className="git-step-marker" aria-hidden="true">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3>{t(step.title)}</h3>
                <p>{t(step.description)}</p>
                <pre dir="ltr">
                  <code>{t(step.code)}</code>
                </pre>
                {index === 0 && <small>{t("git.design.cloneHint")}</small>}
              </li>
            ))}
          </ol>
        </section>

        <section
          className="git-ownership"
          aria-labelledby="git-ownership-title"
        >
          <div className="git-container git-ownership-layout">
            <div className="git-ownership-story">
              <p className="git-eyebrow">{t("git.design.ownershipEyebrow")}</p>
              <h2 id="git-ownership-title" className="git-heading">
                {t("git.design.ownershipTitle")}
              </h2>
              <p className="git-section-description">
                {t("git.design.ownershipDescription")}
              </p>
              <div className="git-portable-file">
                <FileCode2 size={32} strokeWidth={1.4} aria-hidden="true" />
                <div>
                  <span dir="ltr">{t("git.design.filename")}</span>
                  <p>{t("git.design.openFormat")}</p>
                </div>
                <ArrowDown size={20} aria-hidden="true" />
              </div>
            </div>
            <div className="git-benefits">
              {BENEFITS.map(({ icon: Icon, title, description }) => (
                <article key={title}>
                  <Icon size={22} strokeWidth={1.6} aria-hidden="true" />
                  <h3>{t(title)}</h3>
                  <p>{t(description)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="git-container git-section git-video-layout"
          aria-labelledby="git-video-title"
        >
          <div>
            <p className="git-eyebrow">
              <GitBranch size={17} aria-hidden="true" />
              {t("common.howItWorks")}
            </p>
            <h2 id="git-video-title" className="git-heading">
              {t("git.design.videoTitle")}
            </h2>
            <p className="git-section-description">
              {t("git.design.videoDescription")}
            </p>
            <LocaleLink
              to="/docs/Basics/getting-started"
              className="git-text-link"
            >
              {t("git.design.docs")}
              <ArrowRight size={17} aria-hidden="true" />
            </LocaleLink>
          </div>
          <div className="git-video-frame">
            <YouTubeFacade
              videoId="Ef3AUuNpfR8"
              title={t("git.design.videoTitle")}
              playLabel={t("git.design.playVideo")}
            />
          </div>
        </section>

        <section
          className="git-container git-section git-faq-layout"
          aria-labelledby="git-faq-title"
        >
          <div>
            <h2 id="git-faq-title" className="git-heading">
              {t("common.frequentlyAskedQuestions")}
            </h2>
            <p className="git-section-description">
              {t("git.design.faqIntro")}
            </p>
          </div>
          <FaqAccordion
            items={GIT_FAQ_KEYS.map(([question, answer]) => ({
              question: t(question),
              answer: t(answer),
            }))}
          />
        </section>

        <section
          className="git-container git-final-section"
          aria-labelledby="git-final-title"
        >
          <div className="git-final-cta">
            <div className="git-final-graph" aria-hidden="true">
              <GitBranch strokeWidth={0.8} />
              <Plus />
            </div>
            <div className="git-final-copy">
              <p className="git-eyebrow">
                <GitCommitHorizontal size={20} aria-hidden="true" />
                {t("git.getStartedWithGit")}
              </p>
              <h2 id="git-final-title" className="git-heading">
                {t("git.design.finalTitle")}
              </h2>
              <p>{t("git.free30DayTrialNoCredit")}</p>
              <a
                href={productUrl(SIGN_UP_AUTH, "git")}
                className="git-button git-button-primary"
              >
                {t("homepage.startFree")}
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
